/**
 * @package atf-theme/accordions
 */
import camelCase from '../../util/camelCase';

/**
 * The configuration options for initializing a new controller object
 * @typedef {Object} AccordionConfig
 * @property {string} accordionItemSelector
 * @property {string} accordionTitleSelector
 * @property {string} accordionBodySelector
 * @property {string} openAllSelector
 * @property {string} collapseAllSelector
 * @property {string} stateDataAttribute
 */

/**
 * The defaults for initializing an accordion
 * @type {AccordionConfig} accordionConfigDefautls
 */
const accordionConfigDefaults = {
    accordionItemSelector: '.js-atf-accordion-item',
    accordionBodySelector: '.js-atf-accordion-body',
    accordionTitleSelector: '.js-atf-accordion-title',
    collapseAllSelector: '.js-atf-accordion-collapse',
    openAllSelector: '.js-atf-accordion-collapse',
    stateDataAttribute: 'accordion-open',
}

/**
 * @typedef {Object} AccordionNode
 *  - A object containing the nodes for each accordion we registered
 * @property {HTMLElement} accordionItem
 * @property {HTMLElement} accordionTitle
 * @property {HTMLElement} accordionBody
 */

/**
 * A class for initializing and controlling accordions.
 * @class
 * @param {AccordionConfig} config
 * @property {AccordionConfig} config
 * @property {AccordionNode[]} accordionNodes
 */
export default class Accordion {
    constructor(config) {
        this.config = {
            ...accordionConfigDefaults,
            ...config,
        }

      this._initialize();
    }

    _initialize() {
        this._registerAccordionNodes();
        this._setInitialState();
        this._registerListeners();
    }

    _setInitialState() {
        this.accordionNodes.forEach(/** @type {AccordionNode} */ node => {
            node.accordionItem.dataset[camelCase(this.config.stateDataAttribute)] = 'false';
        })
    }

    _registerAccordionNodes() {
        // fetch all the accordion items
            // For each, fetch the title and body within
            // build an array of each accordion item
        // bind the accordion item array to a class prop
        const {
            accordionBodySelector,
            accordionItemSelector,
            accordionTitleSelector,
        } = this.config;

        const accordionItems = document.querySelectorAll(accordionItemSelector);

        if(accordionItems.length <= 0) {
            throw new Error(`No valid items to initialize matching the selector "${accordionItemSelector}"`);
        }

        /**
         * Takes an accordion item HTMLElement and queries it to return an accordion node object.
         * @param {HTMLElement} accordionItem
         * @return {AccordionNode}
         */
        const accordionNodeFactory = (accordionItem) => {
            const accordionTitle = accordionItem.querySelector(accordionTitleSelector);
            const accordionBody = accordionItem.querySelector(accordionBodySelector);

            return {
                accordionBody,
                accordionItem,
                accordionTitle,
            }
        }

        this.accordionNodes = Array.from(accordionItems)
            .map(accordionNodeFactory);
    }

    _registerListeners() {
        /** @param {AccordionNode} node */
        const clickHandlerFactory = node => {
            /** @extends EventCallback */
            const handler = () => {
                const stateDataAttributeKey = camelCase(this.config.stateDataAttribute);
                node.accordionItem.dataset[stateDataAttributeKey] = (node.accordionItem.dataset[stateDataAttributeKey] === 'false').toString();
            }

            return handler;
        }

        this.accordionNodes.forEach(/** @type {AccordionNode} */ node => {
            node.accordionItem.addEventListener('click', clickHandlerFactory(node));
        });

        const stateDataAttributeKey = camelCase(this.config.stateDataAttribute);
        document.querySelector(this.config.openAllSelector)
            .addEventListener('click', () => {
                this.accordionNodes.forEach(/** @type {AccordionNode} */ node => {
                    node.accordionItem.dataset[stateDataAttributeKey] = 'true';
                })
            });
        document.querySelector(this.config.collapseAllSelector)
            .addEventListener('click', () => {
                this.accordionNodes.forEach(/** @type {AccordionNode} */ node => {
                    node.accordionItem.dataset[stateDataAttributeKey] = 'false';
                });
            });
    }
}



// WEBPACK FOOTER //
// ./scripts/modules/accordions/index.js